import React, { FC, useCallback, useEffect, useState } from 'react';
import Toolbar from '../Toolbar/Toolbar';
import ImageActions from '../ImageActions/ImageActions';
import Canvas from '../../../../AnnotationManager/Canvas/Canvas';
import FindingOverlayCard from '../../FindingOverlayCard/FindingOverlayCard';
import { ImageRawData, ImageSettings } from '../../annotation.types';
import { DataQCAssessment } from '../../Tree/QC/qc.slice';
import Effects from '../Effects/Effects';
import { useDebounce } from '../../../../Shared/useDebounce';

type Props = {
  image: ImageRawData | null;
  hideTree: boolean;
  hideStichedView: boolean;
  stitchedViewWidth: number;
  treeWidth: number;
  toggleOverlayVisibility: boolean;
  turbineDataQCAssessment: DataQCAssessment | null;
  showTolbarAndActions: boolean;
  imageSettings: ImageSettings | null;
  setShowQCModal: (value: boolean) => void;
  navigateToFinding: (sequencePath: string, imageName: string, id: string) => void;
  onSaveFilters: (contrast: number, brightness: number, saturation: number, applyToAll: boolean) => void;
  onGetImageSettings: () => void;
};

const CanvasContainer: FC<Props> = ({
  showTolbarAndActions,
  image,
  hideTree,
  hideStichedView,
  stitchedViewWidth,
  treeWidth,
  toggleOverlayVisibility,
  turbineDataQCAssessment,
  imageSettings,
  setShowQCModal,
  navigateToFinding,
  onSaveFilters,
  onGetImageSettings
}) => {
  const [hoveredFinding, setHoveredFinding] = useState<any | undefined>();
  const [showFindingOverlayOnLeft, setShowFindingOverlayOnLeft] = useState<boolean>(true);
  const [showImageEffects, setShowImageEffects] = useState<boolean>(false);

  const [contrast, setContrast] = useState(0);
  const [brightness, setBrightness] = useState(0);
  const [saturation, setSaturation] = useState(0);

  const debouncedContrast = useDebounce(contrast, 100);
  const debouncedBrightness = useDebounce(brightness, 100);
  const debouncedSaturation = useDebounce(saturation, 100);

  const updateOverlayPosition = useCallback(
    (onLeft: boolean) => {
      if (onLeft !== showFindingOverlayOnLeft) {
        setShowFindingOverlayOnLeft(onLeft);
      }
    },
    [showFindingOverlayOnLeft]
  );

  useEffect(() => {
    if (imageSettings) {
      setContrast(imageSettings.contrast);
      setBrightness(imageSettings.brightness);
      setSaturation(imageSettings.saturation);

      return;
    }

    onResetFilters();
  }, [imageSettings]);

  useEffect(() => {
    if (image) {
      onResetFilters();
      onGetImageSettings();
    }
  }, [image, onGetImageSettings]);

  const onResetFilters = () => {
    setContrast(0);
    setBrightness(0);
    setSaturation(0);
  };

  return (
    <div style={{ position: 'relative' }}>
      {toggleOverlayVisibility && hoveredFinding && (
        <FindingOverlayCard finding={hoveredFinding} showFindingOverlayOnLeft={showFindingOverlayOnLeft} />
      )}
      <Canvas
        stitchedViewWidth={stitchedViewWidth}
        hideTree={hideTree}
        hideStichedView={hideStichedView}
        rawImage={image}
        treeWidth={treeWidth}
        setHoveredFinding={setHoveredFinding}
        setShowFindingOverlayOnLeft={updateOverlayPosition}
        onAnnotationSelect={navigateToFinding}
        contrast={debouncedContrast}
        brightness={debouncedBrightness}
        saturation={debouncedSaturation}
      />
      {showTolbarAndActions && <ImageActions />}
      {showTolbarAndActions && (
        <Toolbar
          isDisabled={false}
          imageDataQCAssessment={image!.dataQCAssessment}
          onQC={() => setShowQCModal(true)}
          showQCOption={turbineDataQCAssessment?.haveQC ?? false}
          showImageEffects={showImageEffects}
          setShowImageEffects={setShowImageEffects}
        />
      )}
      {showTolbarAndActions && showImageEffects && (
        <Effects
          {...{
            contrast,
            setContrast,
            brightness,
            setBrightness,
            saturation,
            setSaturation,
            onResetFilters,
            onSaveFilters,
            setShowImageEffects
          }}
        />
      )}
    </div>
  );
};

export default CanvasContainer;
